import React from "react";

const PALETTE = [
  { border: "#7c3aed", bg: "#f5f3ff", text: "#5b21b6" },
  { border: "#0ea5e9", bg: "#f0f9ff", text: "#0369a1" },
  { border: "#059669", bg: "#f0fdf4", text: "#047857" },
  { border: "#d97706", bg: "#fffbeb", text: "#92400e" },
  { border: "#db2777", bg: "#fdf2f8", text: "#9d174d" },
  { border: "#0891b2", bg: "#ecfeff", text: "#0e7490" },
  { border: "#65a30d", bg: "#f7fee7", text: "#3f6212" },
];

function pal(d) { return PALETTE[(d || 0) % PALETTE.length]; }

function fmtCur(val) {
  if (!val && val !== 0) return "—";
  if (val === 0) return "₹0";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(val);
}

function initials(name = "") {
  return name.split(" ").slice(0, 2).map(w => w[0] || "").join("").toUpperCase() || "?";
}

export default function InfoDrawer({ member, onClose }) {
  if (!member) return null;
  const p = pal(member.depth);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 99,
          background: "rgba(15,23,42,0.25)",
          backdropFilter: "blur(3px)",
          animation: "fadeIn 0.2s ease",
        }}
      />

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: 360, maxWidth: "95vw",
        background: "#fff",
        borderLeft: `3px solid ${p.border}`,
        boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
        display: "flex", flexDirection: "column",
        zIndex: 100,
        animation: "slideRight 0.28s cubic-bezier(.4,0,.2,1)",
        overflowY: "auto",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${p.bg}, #fff)`,
          borderBottom: "1px solid #e2e8f0",
          padding: "18px 20px 14px",
          display: "flex", alignItems: "flex-start", gap: 14,
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, flexShrink: 0,
            background: `linear-gradient(135deg, ${p.border}, ${p.text})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 800, fontSize: 18,
          }}>
            {initials(member.name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a", wordBreak: "break-word" }}>
              {member.name}
            </div>
            {member.post_id && (
              <div style={{
                display: "inline-block", marginTop: 4,
                background: p.border, color: "#fff",
                fontSize: 10, fontWeight: 700, padding: "2px 10px",
                borderRadius: 20,
              }}>
                {member.post_id[1]}
              </div>
            )}
            <div style={{ marginTop: 4, fontSize: 11, color: "#94a3b8" }}>
              Level {member.depth ?? 0}{member.mlm_sequence ? ` · #${member.mlm_sequence}` : ""}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 30, height: 30, borderRadius: 8, border: "1px solid #e2e8f0",
              background: "#f8fafc", cursor: "pointer", fontSize: 16, color: "#64748b",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, lineHeight: 1,
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px 24px", display: "flex", flexDirection: "column", gap: 18 }}>

          {/* Network stats */}
          <Section title="📊 Network Stats">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <BigStat label="Direct" value={member.total_direct_child ?? 0} color={p.border} bg={p.bg} />
              <BigStat label="Own Amt" value={fmtCur(member.total_amount)} color={p.border} bg={p.bg} />
              <BigStat label="Team Amt" value={fmtCur(member.child_total_amount)} color={p.border} bg={p.bg} />
            </div>
          </Section>

          {/* Identification */}
          <Section title="🪪 Identification">
            <InfoRow label="Node #"   value={member.mlm_sequence || "—"} mono />
            <InfoRow label="Sponsor"  value={member.parent_id ? member.parent_id[1] : "Root (No Sponsor)"} />
            <InfoRow label="Tree Path" value={member.mlm_treepath || "—"} mono small />
          </Section>

          {/* Status */}
          <Section title="✅ Status">
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 16px", borderRadius: 20,
              background: member.is_mlm_member ? "#f0fdf4" : "#f8fafc",
              border: `1px solid ${member.is_mlm_member ? "#bbf7d0" : "#e2e8f0"}`,
              color: member.is_mlm_member ? "#15803d" : "#64748b",
              fontWeight: 600, fontSize: 13,
            }}>
              {member.is_mlm_member ? "🟢 Active MLM Member" : "⚪ Not an MLM Member"}
            </div>
          </Section>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn    { from { opacity:0; } to { opacity:1; } }
        @keyframes slideRight { from { transform:translateX(100%); } to { transform:translateX(0); } }
        @keyframes spin       { to   { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value, mono, small }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      padding: "8px 12px", background: "#f8fafc", borderRadius: 8,
      border: "1px solid #e2e8f0", marginBottom: 6, gap: 12,
    }}>
      <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500, flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: small ? 11 : 12, fontWeight: 600, color: "#0f172a",
        fontFamily: mono ? "monospace" : "inherit",
        wordBreak: "break-all", textAlign: "right",
      }}>{value}</span>
    </div>
  );
}

function BigStat({ label, value, color, bg }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "10px 6px", background: bg, borderRadius: 10,
      border: `1.5px solid ${color}25`,
    }}>
      <span style={{ fontSize: 13, fontWeight: 800, color }}>{value}</span>
      <span style={{ fontSize: 10, color: "#64748b", fontWeight: 500, marginTop: 2 }}>{label}</span>
    </div>
  );
}
